// app/components/EmploymentModal.tsx
import { Portal, Modal, Text, Button, TextInput, useTheme } from 'react-native-paper';
import { View, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Controller, UseFormReturn, SubmitHandler } from 'react-hook-form';
import { CompanyFormData } from '../lib/types';

interface EmploymentModalProps {
  visible: boolean;
  onDismiss: () => void;
  fromPendingWarning: boolean;
  onBack: () => void;
  form: UseFormReturn<CompanyFormData>;
  onSubmit: (data: CompanyFormData, form: UseFormReturn<CompanyFormData>) => Promise<boolean>;
  onLogout: () => void;
}

export const EmploymentModal = ({ visible, onDismiss, fromPendingWarning, onBack, form, onSubmit, onLogout }: EmploymentModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { control, handleSubmit, formState: { errors, isSubmitting } } = form;

  return (
    <Portal>
      <Modal 
        visible={visible} 
        onDismiss={onDismiss} 
        dismissable={false}
        contentContainerStyle={[
          { backgroundColor: theme.colors.surface, padding: 20, margin: 20, borderRadius: 8 },
          Platform.OS === 'web' ? {
            maxWidth: 500,
            width: '90%',
            alignSelf: 'center',
          } : {}
        ]}
      >
        <Text variant="headlineSmall" style={{ marginBottom: 20 }}>{t('addCompanyInfo')}</Text>
        <Controller
          control={control}
          name="company_name"
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <>
              <TextInput label={t('companyNamePlaceholder')} mode="outlined" onBlur={onBlur} onChangeText={onChange} value={value} error={!!error} style={{ marginBottom: 10 }} />
              {error && <Text style={{ color: theme.colors.error }}>{error.message}</Text>}
            </>
          )}
        />
        <Controller
          control={control}
          name="company_nipc"
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <>
              <TextInput label={t('nipcPlaceholder')} mode="outlined" onBlur={onBlur} onChangeText={onChange} value={value} error={!!error} keyboardType="numeric" style={{ marginBottom: 10 }} />
              {error && <Text style={{ color: theme.colors.error }}>{error.message}</Text>}
            </>
          )}
        />
        <Controller
          control={control}
          name="company_email"
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <>
              <TextInput label={t('companyEmailPlaceholder')} mode="outlined" onBlur={onBlur} onChangeText={onChange} value={value} error={!!error} keyboardType="email-address" style={{ marginBottom: 20 }} />
              {error && <Text style={{ color: theme.colors.error }}>{error.message}</Text>}
            </>
          )}
        />
        <View style={{ flexDirection: 'column', gap: 10 }}>
          <Button mode="contained" onPress={handleSubmit((data) => onSubmit(data, form))} loading={isSubmitting} disabled={isSubmitting}>
            {t('submitButton')}
          </Button>
          {fromPendingWarning && (
            <Button mode="text" icon="arrow-left" onPress={onBack}>
              {t('back')}
            </Button>
          )}
          <Button 
            mode="outlined" 
            onPress={onLogout}
            icon="logout"
            style={{ marginTop: 10 }}
          >
            {t('logout')}
          </Button>
        </View>
      </Modal>
    </Portal>
  );
};